import React, { useState, useCallback, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import Alert from '../../../../components/Backdrops/Alert';
import Dialog from '../../../../components/Backdrops/Dialog';
import Loading from '../../../../components/Backdrops/Loading';
import zebraTagMakerService from '../../../../utils/services/accreditation/zebraTagMaker';
import plusXMiceIdService from '../../../../utils/services/accreditation/plusXMiceId';
import adminUserService from "../../../../utils/services/accreditation/adminUser";
import ZebraTagModel from '../../../../utils/models/ZebraTagModel';
import PlusXMiceIdModel from '../../../../utils/models/PlusXMiceIdModel';

import CommandButtons from "./CommandButtons";
import CrudButtons from "./CrudButtons";
import EventNotActive from "./EventNotActive";
import RenderTab from "./RenderTab";

const DEFAULT_ZPL = `^XA
^ll900
^FO30,40^A0N,62,47^FB760,0,0,L^FD{GUESTNAME}^FS
^FO30,110^A0N,30,30^FB500,1,0,L^FD{TEXT1}^FS
^FO30,150^A0N,30,30^FB500,1,0,L^FD{TEXT2}^FS
^FO30,190^A0N,30,30^FB500,1,0,L^FD{TEXT3}^FS
^FO30,230^A0N,30,30^FB500,1,0,L^FD{TEXT4}^FS
^FO30,270^A0N,30,30^FB500,1,0,L^FD{TEXT5}^FS
^FO30,40^A0N,40,38^FB500,1,0,L^FD0^FH
^FO570,90^BQN,2,5^FDQA,{QRCODE}^FS
^CFR,15
^XZ`;


function ZebraTagMakerExample() {
  const location = useLocation();
  const eventId = location.state.idEvent;

  const [zplCode, setZplCode] = useState(DEFAULT_ZPL);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [widthCm, setWidthCm] = useState('10');
  const [heightCm, setHeightCm] = useState('4');
  const [dpmm, setDpmm] = useState('8dpmm');
  const [tagsList, setTagsList] = useState([]);
  const [currentTag, setCurrentTag] = useState();
  const [tagDescription, setTagDescription] = useState('');
  const [dialogControl, setDialogControl] = useState({ open: false, type: null });
  const [loadingControl, setLoadingControl] = useState({ open: false, message: '' });
  const [alertControl, setAlertControl] = useState({ open: false, title: '', message: '', status: '' });
  const [plusXMiceId, setPlusXMiceId] = useState(PlusXMiceIdModel());
  const plusXMiceIdRef = useRef(plusXMiceId);
  useEffect(() => {
    plusXMiceIdRef.current = plusXMiceId;
  }, [plusXMiceId]);
  const prevBlobUrl = useRef(null);

  useEffect(() => {
    getData();
  }, []);

  const getData = useCallback(async () => {
    try {
      setLoadingControl({ ...loadingControl, open: true, message: 'Carregando' });

      const _plusXMiceId = await plusXMiceIdService.getPlusXMiceId(eventId);
      setPlusXMiceId(_plusXMiceId);
      if (!_plusXMiceId.active) {
        return;
      }

      setTimeout(() => {
        loadTags();
      }, 100);

    } catch (error) {
      console.log('Error getData', error);
      setAlertControl({
        open: true,
        title: 'Erro',
        message: error.message || 'Não foi possível realizar esta operação',
        status: 'error',
      });
    } finally {
      setLoadingControl({ ...loadingControl, open: false });
    }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      if (!plusXMiceIdRef.current.active) {
        return;
      }

      setLoadingControl({ ...loadingControl, open: true, message: 'Carregando' });

      const _tags = (await zebraTagMakerService.getTags()).sort((a, b) => a.description.localeCompare(b.description));
      setTagsList(_tags);

      let _zebraCode = DEFAULT_ZPL;
      if (_tags.length > 0) {
        setZplCode(_tags[0].zebraCode);
        _zebraCode = _tags[0].zebraCode;
        setCurrentTag(_tags[0].id);
      }

      renderPreview(_zebraCode);
    } catch (error) {
      console.log('Error loadTags', error);
      setAlertControl({
        open: true,
        title: 'Erro',
        message: error.message || 'Não foi possível carregar os modelos de etiqueta',
        status: 'error',
      });
    } finally {
      setLoadingControl({ ...loadingControl, open: false });
    }
  }, []);

  const renderPreview = useCallback(async (codeOverride = null) => {
    try {
      setLoadingControl({ open: true, message: 'Renderizando etiqueta...' });

      const codeToRender = codeOverride !== null ? codeOverride : zplCode;
      const blob = await zebraTagMakerService.renderTagPreview({ zplCode: codeToRender, widthCm, heightCm, dpmm });
      const url = URL.createObjectURL(blob);

      if (prevBlobUrl.current) {
        URL.revokeObjectURL(prevBlobUrl.current);
      }
      prevBlobUrl.current = url;
      setPreviewUrl(url);

    } catch (error) {
      console.log('Error renderPreview', error);
      setAlertControl({
        open: true,
        title: 'Erro',
        message: error.message || 'Não foi possível renderizar a etiqueta',
        status: 'error',
      });
    } finally {
      setLoadingControl({ open: false, message: '' });
    }
  }, [zplCode, widthCm, heightCm, dpmm]);

  const handleTagSelect = useCallback((tagId) => {
    const tag = tagsList.find(t => t.id === tagId);
    if (!tag) return;
    setCurrentTag(tag.id);
    setZplCode(tag.zebraCode);
    renderPreview(tag.zebraCode);
  }, [tagsList, renderPreview]);

  const openDeleteDialog = useCallback(() => {
    setDialogControl({ open: true, type: 'delete' });
  }, []);

  const openSaveDialog = useCallback(() => {
    if (!tagDescription.trim()) {
      setAlertControl({ open: true, title: 'Atenção', message: "Campo 'Descrição do template' obrigatório", status: 'warning' });
      return;
    }
    setDialogControl({ open: true, type: 'save' });
  }, [tagDescription]);

  const handleSaveNew = useCallback(async () => {
    setDialogControl({ open: false, type: null });
    try {
      setLoadingControl({ open: true, message: 'Salvando template...' });
      await zebraTagMakerService.createTag(ZebraTagModel({ description: tagDescription, zebraCode: zplCode }));
      setAlertControl({ open: true, title: 'Sucesso', message: 'Template salvo com sucesso!', status: 'success' });
      setTagDescription('');
      loadTags();
    } catch (error) {
      setAlertControl({ open: true, title: 'Erro', message: error.message || 'Não foi possível salvar o template', status: 'error' });
    } finally {
      setLoadingControl({ open: false, message: '' });
    }
  }, [tagDescription, zplCode]);

  const handleUpdateTag = useCallback(async () => {
    setDialogControl({ open: false, type: null });
    try {
      setLoadingControl({ open: true, message: 'Atualizando template...' });
      const currentTagObj = tagsList.find(t => t.id === currentTag);
      await zebraTagMakerService.updateTag(ZebraTagModel({ ...currentTagObj, zebraCode: zplCode }));
      setAlertControl({ open: true, title: 'Sucesso', message: 'Template atualizado com sucesso!', status: 'success' });
      loadTags();
    } catch (error) {
      setAlertControl({ open: true, title: 'Erro', message: error.message || 'Não foi possível atualizar o template', status: 'error' });
    } finally {
      setLoadingControl({ open: false, message: '' });
    }
  }, [tagsList, zplCode, currentTag]);

  const handleDeleteTag = useCallback(async () => {
    setDialogControl({ open: false, type: null });
    try {
      setLoadingControl({ open: true, message: 'Deletando template...' });
      await zebraTagMakerService.deleteTag(currentTag);
      setAlertControl({ open: true, title: 'Sucesso', message: 'Template deletado com sucesso!', status: 'success' });
      loadTags();
    } catch (error) {
      setAlertControl({ open: true, title: 'Erro', message: error.message || 'Não foi possível deletar o template', status: 'error' });
    } finally {
      setLoadingControl({ open: false, message: '' });
    }
  }, [currentTag]);

  const addTagToEvent = useCallback(async () => {
    try {
      setLoadingControl({ open: true, message: 'Aplicando etiqueta ao evento...' });

      await adminUserService.saveConfigApi(
        plusXMiceIdRef.current.miceId,
        8,
        "6"
      );

      await adminUserService.saveConfigApi(
        plusXMiceIdRef.current.miceId,
        22,
        zplCode
      );

      setAlertControl({ open: true, title: 'Sucesso', message: 'Etiqueta aplicada com sucesso!', status: 'success' });

    } catch (error) {
      setAlertControl({ open: true, title: 'Erro', message: error.message || 'Não foi possível aplicar a etiqueta ao evento', status: 'error' });
    } finally {
      setLoadingControl({ open: false, message: '' });
    }
  }, [currentTag, plusXMiceIdRef, zplCode]);

  return (
    <div style={{ padding: '20px' }}>
      {!plusXMiceId.active && (
        <EventNotActive />
      )}
      {plusXMiceId.active && (
        <>
          <CommandButtons
            handleTagSelect={handleTagSelect}
            tagsList={tagsList}
            currentTag={currentTag}
            widthCm={widthCm}
            setWidthCm={setWidthCm}
            heightCm={heightCm}
            setHeightCm={setHeightCm}
            dpmm={dpmm}
            setDpmm={setDpmm}
            renderPreview={renderPreview}
          />

          <RenderTab
            zplCode={zplCode}
            setZplCode={setZplCode}
            previewUrl={previewUrl}
          />

          <CrudButtons
            tagDescription={tagDescription}
            setTagDescription={setTagDescription}
            openSaveDialog={openSaveDialog}
            setDialogControl={setDialogControl}
            openDeleteDialog={openDeleteDialog}
            addTagToEvent={addTagToEvent}
          />
        </>
      )}

      <Loading loadingControl={loadingControl} />
      <Alert alertControl={alertControl} setAlertControl={setAlertControl} />
      <Dialog
        open={dialogControl.open}
        title={dialogControl.type === 'save' ? 'Salvar Novo Template' : dialogControl.type === 'update' ? 'Atualizar Template' : 'Deletar Template'}
        message={dialogControl.type === 'save'
          ? 'Tem certeza que deseja salvar um novo template?'
          : dialogControl.type === 'update'
            ? 'Tem certeza que deseja atualizar o template selecionado?'
            : 'Tem certeza que deseja deletar o template selecionado?'}
        button1OnPress={dialogControl.type === 'save' ? handleSaveNew : dialogControl.type === 'update' ? handleUpdateTag : handleDeleteTag}
        button2OnPress={() => setDialogControl({ open: false, type: null })}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return { state };
};

export default connect(mapStateToProps)(ZebraTagMakerExample);
